

import axios from 'axios';
import { useMapEvent, useMapEvents } from 'react-leaflet/hooks'
import { GeoJSON } from 'react-leaflet/GeoJSON';
import * as turf from "@turf/turf";
import { useState, useRef, useEffect, useCallback } from "react"
//import { Popup } from 'react-leaflet';
import Select from 'react-select'
import { defaultTenementTypes, tenementTypes } from './TenementTypeData';
import Table from 'react-bootstrap/Table';
import { formatDate } from '../CustomDateTools';
import {
    MapContainer, TileLayer, LayersControl,
    WMSTileLayer, ScaleControl,
    Marker
} from 'react-leaflet';
import {
    Map,
    NavigationControl,
    FullscreenControl,
    GeolocateControl,
    Source,
    Layer,
    useMap,
    Popup
} from 'react-map-gl/maplibre';


const GoldFinderGeoserver = axios.create({
    baseURL: process.env.REACT_APP_GOLD_FINDER_GEOSERVER
})

export function lookupSingleTenementId(tenid, setTenementPopup, goldfindermap, setShowTenementPopup, trigger40EWizard) {
    // lookup the tenement, then pan the map to it.
    // Construct the filter query

    const filterQuery = `
    <Filter>
        <PropertyIsEqualTo>
            <PropertyName>fmt_tenid</PropertyName>
            <Literal>${tenid}</Literal>
        </PropertyIsEqualTo>
    </Filter>
    `;

    // First, try to find in regular tenements
    GoldFinderGeoserver.get("/ne/ows?", {
        params: {
            service: 'WFS',
            version: '1.0.0',
            request: 'GetFeature',
            typeNames: 'ne:wa_tenements',
            maxFeatures: 99999,
            outputFormat: 'application/json',
            srs: 'EPSG:4326',
            filter: filterQuery
        }
    }).then((e) => {

        if (e.data.features.length === 0) {
            // Not found in regular tenements, try amalgamations pending
            searchAmalgamationsPending(tenid, setTenementPopup, goldfindermap, setShowTenementPopup);
            return;
        }

        e.data.features.map(tenement => {
            tenement.properties.startdate_obj = new Date(Date.parse(tenement.properties.startdate));
            tenement.properties.enddate_obj = new Date(Date.parse(tenement.properties.enddate));
            tenement.properties.grantdate_obj = new Date(Date.parse(tenement.properties.grantdate));
            return tenement;
        });

        const turfBounds = turf.bbox(e.data);
        goldfindermap.fitBounds([
            [turfBounds[0], turfBounds[1]],
            [turfBounds[2], turfBounds[3]]])

        const lng = turf.centerOfMass(e.data.features[0].geometry).geometry.coordinates[0];
        const lat = turf.centerOfMass(e.data.features[0].geometry).geometry.coordinates[1];

        setTenementPopup(
            createSingleTenementPopupTable(
                e.data.features[0], { lat: lat, lng: lng },
                setShowTenementPopup, trigger40EWizard)
        )

        setShowTenementPopup(true);

    }).catch((error) => {
        console.error("Error searching tenements:", error);
        // Try amalgamations pending as fallback
        searchAmalgamationsPending(tenid, setTenementPopup, goldfindermap, setShowTenementPopup);
    });
}

// Search for tenement in amalgamations pending layer
function searchAmalgamationsPending(tenid, setTenementPopup, goldfindermap, setShowTenementPopup) {
    const SlipWfsServer = axios.create({
        baseURL: "https://public-services.slip.wa.gov.au/public/services/SLIP_Public_Services/Industry_and_Mining_WFS/MapServer/WFSServer",
        timeout: 30000
    });

    SlipWfsServer.get("", {
        params: {
            service: 'WFS',
            version: '2.0.0',
            request: 'GetFeature',
            typeNames: 'esri:Tenements_-_Amalgamation_pending__DMIRS-031_',
            outputFormat: 'GEOJSON',
            srsName: 'EPSG:4326',
            CQL_FILTER: `fmt_tenid='${tenid}'`
        }
    }).then((response) => {
        if (!response.data.features || response.data.features.length === 0) {
            alert("No Tenements found with ID: " + tenid);
            return;
        }

        // Fix coordinate order - ESRI returns [lat, lng] but GeoJSON needs [lng, lat]
        const fixCoordinates = (coords) => {
            if (!coords) return coords;
            if (typeof coords[0] === 'number' && typeof coords[1] === 'number') {
                return [coords[1], coords[0]]; // Swap to [lng, lat]
            }
            return coords.map(fixCoordinates);
        };

        const feature = {
            ...response.data.features[0],
            geometry: {
                ...response.data.features[0].geometry,
                coordinates: fixCoordinates(response.data.features[0].geometry.coordinates)
            }
        };

        const turfBounds = turf.bbox(feature);
        goldfindermap.fitBounds([
            [turfBounds[0], turfBounds[1]],
            [turfBounds[2], turfBounds[3]]
        ]);

        const lng = turf.centerOfMass(feature.geometry).geometry.coordinates[0];
        const lat = turf.centerOfMass(feature.geometry).geometry.coordinates[1];

        // Import and use the amalgamations pending popup
        import('./AmalgamationsPendingOverlay').then(module => {
            setTenementPopup(
                module.createAmalgamationsPendingPopup(feature, { lat: lat, lng: lng }, setShowTenementPopup)
            );
            setShowTenementPopup(true);
        });

    }).catch((error) => {
        console.error("Error searching amalgamations pending:", error);
        alert("No Tenements found with ID: " + tenid);
    });
}

function calculateDateGap(start, end) {
    var ageDifMs = end - start;
    var ageDate = new Date(ageDifMs); // miliseconds from epoch
    return Math.abs(ageDate.getUTCFullYear() - 1970);
}

export function queryTenementWFS(map, tenementFilters, setStateGeoJSON) {
    const filterQuery = ` 
    <Filter> 
    <And>
        <Or> 
        ${tenementFilters.types.map(element => {
        return `
            <PropertyIsEqualTo> 
                <PropertyName>type</PropertyName> 
                <Literal>${element.value}</Literal> 
            </PropertyIsEqualTo> `;
    })}
        </Or>
        <Or>
        ${tenementFilters.includeLive ? `
            <PropertyIsEqualTo> 
                <PropertyName>tenstatus</PropertyName> 
                <Literal>LIVE</Literal> 
            </PropertyIsEqualTo> `: ""}
        ${tenementFilters.overridePendingTenementDisplay || tenementFilters.includePending ? `
            <PropertyIsEqualTo> 
                <PropertyName>tenstatus</PropertyName> 
                <Literal>PENDING</Literal> 
            </PropertyIsEqualTo> `: ""}
        </Or>
        <BBOX>
            <PropertyName>geom</PropertyName>
            <Box srsName="urn:x-ogc:def:crs:EPSG:4326">
                  <gml:coordinates>${map.getBounds().toBBoxString()}</gml:coordinates>
            </Box>
        </BBOX>
    </And>
    </Filter> 
    `;

    const getTenStatusFilter = () => {
        const filters = [];
        if (tenementFilters.includeLive) {
            filters.push('LIVE')
        }
        if (tenementFilters.includePending) {
            filters.push('PENDING')
        }
        if (tenementFilters.includeDead) {
            filters.push('Dead')
        }

        if (filters.length === 0) return "";

        if (filters.length != 1) {
            return `tenstatus in ('${filters.join(`','`)}')`
        } else {
            return `tenstatus='${filters.pop()}'`
        }
    };


    const getSurveyedStatusfilter = () => {
        const filters = [];
        if (tenementFilters.includeSurveyed) {
            filters.push('SURVEYED')
            filters.push('Surveyed')
        }
        if (tenementFilters.includeUnsurveyed) {
            filters.push('UNSURVEYED')
            filters.push('Unsurveyed')
        }

        if (filters.length === 0) return "";

        return `survstatus in ('${filters.join(`','`)}')`

    }

    const getDeadSplFilter = () => {
        if (tenementFilters.deadSpls) {
            return `
            or
            (type = 'SPECIAL PROSPECTING LICENCE'
            and
            tenstatus= 'Dead')
            `
        } else {
            return ""
        }
    }

    const tenementTypes = tenementFilters.types.map(element => {
        return element.value;
    });



    const standardFilter = `
      ${tenementFilters.includeSurveyed || tenementFilters.includeUnsurveyed ? "and" : ""}
        ${getSurveyedStatusfilter()}
        and
        ((
        ${getTenStatusFilter()}
        and
        type in ('${tenementTypes.join(`','`)}')
        )
        ${getDeadSplFilter()}
        )
        and
        startdate AFTER ${tenementFilters.startDate.toISOString()}
        and 
        enddate before ${tenementFilters.endDate.toISOString()}
        and
        grantdate ${tenementFilters.grantDateBeforeAfter === 'AFTER' ? "AFTER" : 'BEFORE'} ${tenementFilters.grantDate.toISOString()}
        ${tenementFilters.grantedOnly ? "and grantdate BEFORE 2999-01-01T00:00:00.000Z" : ""}
        ${tenementFilters.holderNameLike === '' ? '' : `and (holdername ILIKE '%${tenementFilters.holderNameLike.split(' ').join('%')}%' `}

    `

    const expiringSoonFilter = `
    and
    (
    tenstatus = 'PENDING'
    or
    (
    tenstatus = 'LIVE'
    and
    type in ('${tenementTypes.join(`','`)}')
    and
    enddate before ${tenementFilters.endDate.toISOString()}
    )
    )

    `

    const ecqlFilter = `
        BBOX(geom,${map.getBounds().toBBoxString()})
        ${tenementFilters.expiringSoonEnabled ? expiringSoonFilter : standardFilter}
        `




    GoldFinderGeoserver.get("/ne/ows?", {
        params: {
            service: 'WFS',
            version: '1.1.0',
            request: 'GetFeature',
            typeNames: 'ne:wa_tenements',
            maxFeatures: 99999,

            outputFormat: 'application/json',
            srs: 'EPSG:4326',
            //bbox: map.getBounds().toBBoxString(),
            //filter: filterQuery
            cql_filter: ecqlFilter
        }
    }).then((e) => {
        // filter tenemements
        // check if there are any filters active. 

        // transform date's into "Date" objects


        e.data.features.map(tenement => {
            tenement.properties.startdate_obj = new Date(Date.parse(tenement.properties.startdate));
            tenement.properties.enddate_obj = new Date(Date.parse(tenement.properties.enddate));
            tenement.properties.grantdate_obj = new Date(Date.parse(tenement.properties.grantdate));
            return tenement;
        });



        if (tenementFilters.expiringSoonEnabled) {
            if (tenementFilters.ignoreFirstTermPl) {
                e.data.features = e.data.features.filter((ten) => {


                    if (ten.properties.type == "PROSPECTING LICENCE") {
                        if (calculateDateGap(ten.properties.grantdate_obj, ten.properties.enddate_obj) > 4) {
                            return true;
                        }
                    } else { return true }
                });
            }

            if (tenementFilters.overridePendingTenementDisplay) {
                // lets check if there are any tenements that are intersecting with this pending one
                // dont bother checking other PENDING tenements
                e.data.features = e.data.features.filter((ten) => {
                    if (ten.properties.tenstatus === "PENDING") {
                        const intersectingFeat = e.data.features.filter((f) => {
                            if (f.properties.tenstatus !== "PENDING") {
                                if (turf.intersect(turf.featureCollection([ten, f])) !== null) {
                                    return true
                                } else {
                                    return false;
                                }
                            }
                        })
                        if (intersectingFeat.length !== 0) {
                            return true
                        } else {
                            return false;
                        }
                    } else { return true }
                })
            }

        }

        setStateGeoJSON(e.data);
    });
}

export function getTenementTypeList() {

    return tenementTypes;
}

export function getDefaultTenementTypeList() {
    return defaultTenementTypes;
}

export const displayTenementHolder = (holdersString) => {
    if (holdersString === undefined) return <></>

    let holdersArrr = holdersString.split(";");

    if (holdersArrr.length === 1) {
        return holdersArrr[0];
    }
    return <ul>
        {holdersArrr.map((holder) => {
            return <li>{holder}</li>
        })}
    </ul>

}

export function TenementOverlay({ stateGeoJSON, setStateGeoJSON, selectedFeature, setSelectedFeature, tenementFilters }) {
    const emptyGeoJson = {
        "type": "FeatureCollection",
        "name": "Mining_Tenements_DMIRS_003",
        "crs": { "type": "name", "properties": { "name": "urn:ogc:def:crs:EPSG::4283" } },
        "features": []
    };

    const [selectedTenementPopup, setSelectedTenementPopup] = useState(<></>);
    // configure monitor on moveend

    const [display, setDisplay] = useState(false);

    const overlayName = "Tenement Overlay";



    useEffect(() => {
        if (selectedFeature.type != undefined) {
            const centerPoint = {
                lat: selectedFeature.clickPoint ? selectedFeature.clickPoint.lat : turf.centerOfMass(selectedFeature.geometry).geometry.coordinates[1],
                lng: selectedFeature.clickPoint ? selectedFeature.clickPoint.lng : turf.centerOfMass(selectedFeature.geometry).geometry.coordinates[0]
            }

            setSelectedTenementPopup(
                <Popup autoPan={false} position={centerPoint}>

                    <Table striped bordered >
                        <tbody>
                            <tr>
                                <td>ID:</td>
                                <td>{selectedFeature.properties.fmt_tenid}</td>
                            </tr>
                            <tr>
                                <td>Type:</td>
                                <td>{selectedFeature.properties.type}</td>
                            </tr>
                            <tr>
                                <td>Status:</td>
                                <td>{selectedFeature.properties.tenstatus}</td>
                            </tr>
                            <tr>
                                <td>Survey Status:</td>
                                <td>{selectedFeature.properties.survstatus}</td>
                            </tr>
                            <tr>
                                <td>Area:</td>
                                <td>{selectedFeature.properties.legal_area} ({selectedFeature.properties.unit_of_me})</td>
                            </tr>
                            <tr>
                                <td>Holder(s):</td>
                                <td>{displayTenementHolder(selectedFeature.properties.holdername)}</td>
                            </tr>
                            <tr>
                                <td>Start Date:</td>
                                <td>{formatDate(selectedFeature.properties.startdate_obj)}</td>
                            </tr>
                            <tr>
                                <td>End Date:</td>
                                <td>{formatDate(selectedFeature.properties.enddate_obj)}</td>
                            </tr>
                            <tr>
                                <td>Grant Date:</td>
                                <td>{formatDate(selectedFeature.properties.grantdate_obj)}</td>
                            </tr>
                        </tbody>
                    </Table>
                </Popup>
            );

        }
    }, [selectedFeature])




    const map = useMapEvents({
        moveend() {
            if (map.getZoom() >= 8 && display) { // this is at 5km scale
                queryTenementWFS(map, tenementFilters, setStateGeoJSON);
            } else {
                setStateGeoJSON(emptyGeoJson);
                setSelectedTenementPopup(<></>);
            }

        }, overlayadd(e) {
            if (e.name == overlayName) {
                setDisplay(true);
                queryTenementWFS(map, tenementFilters, setStateGeoJSON);
            }
        }, overlayremove(e) {
            if (e.name == overlayName) {
                setSelectedTenementPopup(<></>);
                setDisplay(false);
            }
        },

        click(e) {

            if (display) {
                var pt = turf.point([e.latlng.lng, e.latlng.lat]);

                // const tempGeoJson = emptyGeoJson;
                //const selectedFeatures = [];
                const selectedFeatures = stateGeoJSON.features.filter((feature) => turf.booleanPointInPolygon(pt, feature.geometry))

                if (selectedFeatures.length == 1) {
                    if (selectedFeature.properties !== undefined) {
                        if (selectedFeature.properties.gid === selectedFeatures[0].properties.gid) {
                            setSelectedFeature({});
                            return;
                        }
                    }
                    selectedFeatures[0].clickPoint = e.latlng;

                    setSelectedFeature(selectedFeatures[0]);

                } else if (selectedFeatures.length > 1) {
                    setSelectedFeature({});

                    setSelectedTenementPopup(
                        <Popup autoPan={false} position={e.latlng}>
                            {selectedFeatures.map((feature) => {
                                return (<p><a href="#" onClick={() => { setSelectedFeature(feature) }}>{feature.properties.fmt_tenid}</a></p>)
                            })}
                        </Popup>
                    );
                }

            }

        }
    });



    // get a ref to the underlying L.geoJSON
    const geoJsonRef = useRef()

    // set the data to new data whenever it changes
    useEffect(() => {
        if (geoJsonRef.current) {
            geoJsonRef.current.clearLayers()   // remove old data
            geoJsonRef.current.addData(stateGeoJSON) // might need to be geojson.features
        }
    }, [geoJsonRef, stateGeoJSON])

    useEffect(() => {
        queryTenementWFS(map, tenementFilters, setStateGeoJSON);
    }, [tenementFilters])


    function districtStyleDefault(feature) {
        if (selectedFeature.properties !== undefined) {
            if (feature.properties.gid === selectedFeature.properties.gid) {
                return { color: 'yellow' }
            }
        }


        if (feature.properties.tenstatus == 'LIVE') {
            return { color: 'green' }

        } else if (feature.properties.tenstatus == 'Dead') {
            if (feature.properties.type == "SPECIAL PROSPECTING LICENCE") {
                return { color: 'red' }
            }
            return { color: 'grey' }

        } else if (feature.properties.tenstatus == 'PENDING') {
            return { color: 'blue' }
        }
        else {
        }
        return { color: 'red' }
    }


    return (
        <div>
            <LayersControl.Overlay name={overlayName}>
                <GeoJSON
                    ref={geoJsonRef}
                    style={districtStyleDefault}
                    data={stateGeoJSON.features}
                />
                {selectedTenementPopup}
            </LayersControl.Overlay>

        </div>)
}

/////// NEW SHIT

export function createSingleTenementPopupTable(tenement, latLng, setClosed, trigger40EWizard) {
    return <Popup latitude={latLng.lat} longitude={latLng.lng}
        anchor="bottom"
        onClose={() => setClosed(false)}>
        <Table striped bordered className="mb-2" style={{ marginBottom: 0 }} >
            <tbody>
                <tr>
                    <td>ID:</td>
                    <td>{tenement.properties.fmt_tenid}</td>
                </tr>
                <tr>
                    <td>Type:</td>
                    <td>{tenement.properties.type}</td>
                </tr>
                <tr>
                    <td>Status:</td>
                    <td>{tenement.properties.tenstatus}</td>
                </tr>
                <tr>
                    <td>Survey Status:</td>
                    <td>{tenement.properties.survstatus}</td>
                </tr>
                <tr>
                    <td>Area:</td>
                    <td>{tenement.properties.legal_area} ({tenement.properties.unit_of_me})</td>
                </tr>
                <tr>
                    <td>Holder(s):</td>
                    <td>{displayTenementHolder(tenement.properties.holdername)}</td>
                </tr>
                <tr>
                    <td>Start Date:</td>
                    <td>{formatDate(new Date(Date.parse(tenement.properties.startdate)))}</td>
                </tr>
                <tr>
                    <td>End Date:</td>
                    <td>{formatDate(new Date(Date.parse(tenement.properties.enddate)))}</td>
                </tr>
                <tr>
                    <td>Grant Date:</td>
                    <td>{formatDate(new Date(Date.parse(tenement.properties.grantdate)))}</td>
                </tr>
            </tbody>
        </Table>
        {tenement.properties.type === "EXPLORATION LICENCE" && tenement.properties.tenstatus === 'LIVE'?
            <button  className="ten-wizard btn btn-secondary btn-sm " onClick={() => trigger40EWizard(tenement)}>Create Section 40E Permit</button>
            : <></>}
    </Popup >
}


export function queryTenementWFSLibre(bboxString, tenementFilters, setStateGeoJSON) {

    const filterQuery = ` 
    <Filter> 
    <And>
        <Or> 
        ${tenementFilters.types.map(element => {
        return `
            <PropertyIsEqualTo> 
                <PropertyName>type</PropertyName> 
                <Literal>${element.value}</Literal> 
            </PropertyIsEqualTo> `;
    })}
        </Or>
        <Or>
        ${tenementFilters.includeLive ? `
            <PropertyIsEqualTo> 
                <PropertyName>tenstatus</PropertyName> 
                <Literal>LIVE</Literal> 
            </PropertyIsEqualTo> `: ""}
        ${tenementFilters.overridePendingTenementDisplay || tenementFilters.includePending ? `
            <PropertyIsEqualTo> 
                <PropertyName>tenstatus</PropertyName> 
                <Literal>PENDING</Literal> 
            </PropertyIsEqualTo> `: ""}
        </Or>
        <BBOX>
            <PropertyName>geom</PropertyName>
            <Box srsName="urn:x-ogc:def:crs:EPSG:4326">
                  <gml:coordinates>${bboxString}</gml:coordinates>
            </Box>
        </BBOX>
    </And>
    </Filter> 
    `;

    const getTenStatusFilter = () => {
        const filters = [];
        if (tenementFilters.includeLive) {
            filters.push('LIVE')
        }
        if (tenementFilters.includePending) {
            filters.push('PENDING')
        }
        if (tenementFilters.includeDead) {
            filters.push('Dead')
        }

        if (filters.length === 0) return `tenstatus='potato' and`;

        if (filters.length != 1) {
            return `tenstatus in ('${filters.join(`','`)}') and`
        } else {
            return `tenstatus='${filters.pop()}' and`
        }
    };


    const getSurveyedStatusfilter = () => {
        const filters = [];
        if (tenementFilters.includeSurveyed) {
            filters.push('SURVEYED')
            filters.push('Surveyed')
        }
        if (tenementFilters.includeUnsurveyed) {
            filters.push('UNSURVEYED')
            filters.push('Unsurveyed')
        }

        if (filters.length === 0) return "";

        return `survstatus in ('${filters.join(`','`)}')`

    }

    const getDeadSplFilter = () => {
        if (tenementFilters.deadSpls) {
            return `
            or
            (type = 'SPECIAL PROSPECTING LICENCE'
            and
            tenstatus= 'Dead')
            `
        } else {
            return ""
        }
    }

    const tenementTypes = tenementFilters.types.map(element => {
        return element.value;
    });

// holdername ILIKE '%${tenementFilters.holderNameLike.split(' ').join('%')}%' 

    const standardFilter = `
      ${tenementFilters.includeSurveyed || tenementFilters.includeUnsurveyed ? "and" : ""}
        ${getSurveyedStatusfilter()}
        and
        ((
        ${getTenStatusFilter()}
        
        type in ('${tenementTypes.join(`','`)}')
        )
        ${getDeadSplFilter()}
        )
        and
        startdate AFTER ${tenementFilters.startDate.toISOString()}
        and 
        enddate before ${tenementFilters.endDate.toISOString()}
        and
        grantdate ${tenementFilters.grantDateBeforeAfter === 'AFTER' ? "AFTER" : 'BEFORE'} ${tenementFilters.grantDate.toISOString()}
        ${tenementFilters.grantedOnly ? "and grantdate BEFORE 2999-01-01T00:00:00.000Z" : ""}
        ${tenementFilters.holderNameLike === '' ? '' : `and (

        ${tenementFilters.holderNameLike.split(' ').map((e,index) =>{ return `${index !==0 ? 'and' : ''} holdername ILIKE '%${e}%'` }).join(' ')}

        )`}

    `

    const expiringSoonFilter = `
    and
    (
    tenstatus = 'PENDING'
    or
    (
    tenstatus = 'LIVE'
    and
    type in ('${tenementTypes.join(`','`)}')
    and
    enddate before ${tenementFilters.endDate.toISOString()}
    )
    )

    `

    const ecqlFilter = `
        BBOX(geom,${bboxString})
        ${tenementFilters.expiringSoonEnabled ? expiringSoonFilter : standardFilter}
        `

    GoldFinderGeoserver.get("/ne/ows?", {
        params: {
            service: 'WFS',
            version: '1.1.0',
            request: 'GetFeature',
            typeNames: 'ne:wa_tenements',
            maxFeatures: 99999,

            outputFormat: 'application/json',
            srs: 'EPSG:4326',
            //bbox: map.getBounds().toBBoxString(),
            //filter: filterQuery
            cql_filter: ecqlFilter
        }
    }).then((e) => {
        // filter tenemements
        // check if there are any filters active. 

        // transform date's into "Date" objects
        if (e.data.features === undefined) return;

        e.data.features.map(tenement => {
            tenement.properties.startdate_obj = new Date(Date.parse(tenement.properties.startdate));
            tenement.properties.enddate_obj = new Date(Date.parse(tenement.properties.enddate));
            tenement.properties.grantdate_obj = new Date(Date.parse(tenement.properties.grantdate));
            return tenement;
        });



        if (tenementFilters.expiringSoonEnabled) {
            if (tenementFilters.ignoreFirstTermPl) {
                e.data.features = e.data.features.filter((ten) => {
                    if (ten.properties.type == "PROSPECTING LICENCE") {
                        if (calculateDateGap(ten.properties.grantdate_obj, ten.properties.enddate_obj) > 4) {
                            return true;
                        }
                    } else { return true }
                });
            }

            if (tenementFilters.overridePendingTenementDisplay) {
                // lets check if there are any tenements that are intersecting with this pending one
                // dont bother checking other PENDING tenements
                e.data.features = e.data.features.filter((ten) => {
                    if (ten.properties.tenstatus === "PENDING") {
                        const intersectingFeat = e.data.features.filter((f) => {
                            if (f.properties.tenstatus !== "PENDING") {
                                if (turf.intersect(turf.featureCollection([ten, f])) !== null) {
                                    return true
                                } else {
                                    return false;
                                }
                            }
                        })
                        if (intersectingFeat.length !== 0) {
                            return true
                        } else {
                            return false;
                        }
                    } else { return true }
                })
            }

        }

        setStateGeoJSON(e.data);
    });
}


export function TenementOverlayMapLibre({ tenementFilters, overlayVisibility, selectedTenement, geoJson, setGeojson }) {
    const emptyGeoJson = {
        "type": "FeatureCollection",
        "name": "Mining_Tenements_DMIRS_003",
        "crs": { "type": "name", "properties": { "name": "urn:ogc:def:crs:EPSG::4283" } },
        "features": []
    };

    const { goldfindermap } = useMap(); // `mymap` is the id in <Map id="mymap" />

    useEffect(() => {
        if (!goldfindermap) return undefined;
        if (goldfindermap.getZoom() > 8) {
            const bounds = goldfindermap.getBounds();
            var wfsbbox = [bounds.getSouthWest().lng, bounds.getSouthWest().lat, bounds.getNorthEast().lng, bounds.getNorthEast().lat].join();
            queryTenementWFSLibre(wfsbbox, tenementFilters, setGeojson)

        }
    }, [tenementFilters])

    const liveLayerFill = {
        id: 'live-tenements',
        minzoom: 8,
        type: 'fill',
        filter: ['==', 'tenstatus', 'LIVE'],
        paint: { 'fill-color': '#008000', 'fill-opacity': 0.2 },
        layout: { visibility: `${overlayVisibility.tenements ? "visible" : "none"}` }
    };

    const liveLayerOutline = {
        id: 'live-tenements-outline',
        minzoom: 8,
        type: 'line',
        filter: ['==', 'tenstatus', 'LIVE'],
        paint: { 'line-color': "#008000", 'line-width': 2 },
        layout: { visibility: `${overlayVisibility.tenements ? "visible" : "none"}` }
    };

    const pendingLayerFill = {
        id: 'pending-tenements',
        minzoom: 8,
        type: 'fill',
        filter: ['==', 'tenstatus', 'PENDING'],
        paint: { 'fill-color': '#0000FF', 'fill-opacity': 0.2 },
        layout: { visibility: `${overlayVisibility.tenements ? "visible" : "none"}` }
    };

    const pendingLayerOutline = {
        id: 'pending-tenements-outline',
        minzoom: 8,
        type: 'line',
        filter: ['==', 'tenstatus', 'PENDING'],
        paint: { 'line-color': "#0000FF", 'line-width': 2 },
        layout: { visibility: `${overlayVisibility.tenements ? "visible" : "none"}` }
    };

    const deadLayerOutline = {
        id: 'dead-tenements-outline',
        minzoom: 8,
        type: 'line',
        filter: ['all', ['!=', 'type', 'SPECIAL PROSPECTING LICENCE'], ['==', 'tenstatus', 'Dead']],
        paint: { 'line-color': "#808080", 'line-width': 2 },
        layout: { visibility: `${overlayVisibility.tenements ? "visible" : "none"}` }
    };
    const deadLayerFill = {
        id: 'dead-tenements',
        minzoom: 8,
        type: 'fill',
        filter: ['all', ['!=', 'type', 'SPECIAL PROSPECTING LICENCE'], ['==', 'tenstatus', 'Dead']],
        paint: { 'fill-color': '#808080', 'fill-opacity': 0.2 },
        layout: { visibility: `${overlayVisibility.tenements ? "visible" : "none"}` }
    };

    const deadSPLLayerOutline = {
        id: 'dead-spl-tenements-outline',
        minzoom: 8,
        type: 'line',
        filter: ['all', ['==', 'type', 'SPECIAL PROSPECTING LICENCE'], ['==', 'tenstatus', 'Dead']],
        paint: { 'line-color': "#ff0000", 'line-width': 2 },
        layout: { visibility: `${overlayVisibility.tenements ? "visible" : "none"}` }
    };
    const deadSPLLayerFill = {
        id: 'dead-spl-tenements',
        minzoom: 8,
        type: 'fill',
        filter: ['all', ['==', 'type', 'SPECIAL PROSPECTING LICENCE'], ['==', 'tenstatus', 'Dead']],
        paint: { 'fill-color': '#ff0000', 'fill-opacity': 0.2 },
        layout: { visibility: `${overlayVisibility.tenements ? "visible" : "none"}` }
    };

    const selectedTenementFill = {
        id: 'selected-tenement-fill',
        minzoom: 8,
        type: 'fill',
        filter: ['==', 'gid', selectedTenement.properties.gid],
        paint: { 'fill-color': '#FFFF00', 'fill-opacity': 0.2 },
        layout: { visibility: `${overlayVisibility.tenements ? "visible" : "none"}` }
    };
    const selectedTenementOutline = {
        id: 'selected-tenement-outline',
        minzoom: 8,
        type: 'line',
        filter: ['==', 'gid', selectedTenement.properties.gid],
        paint: { 'line-color': "#FFFF00", 'line-width': 2 },
        layout: { visibility: `${overlayVisibility.tenements ? "visible" : "none"}` }
    };



    return <Source id="tenements" type="geojson" data={geoJson}>
        <Layer  {...liveLayerFill} />
        <Layer {...liveLayerOutline} />
        <Layer {...pendingLayerFill} />
        <Layer  {...pendingLayerOutline} />
        <Layer  {...deadSPLLayerFill} />
        <Layer  {...deadSPLLayerOutline} />
        <Layer  {...deadLayerFill} />
        <Layer  {...deadLayerOutline} />
        <Layer {...selectedTenementFill} />
        <Layer {...selectedTenementOutline} />

    </Source>

}